import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Faltan las variables de entorno de Supabase: VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// ===== TYPES =====
export interface Post {
  id: string
  title: string
  slug: string
  cover_image_url?: string
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
  published_at?: string
  created_at: string
  updated_at: string
  author_id: string
}

export interface Tag {
  id: string
  name: string
  slug: string
  description?: string
  created_at: string
}

// ===== BLOG API =====
export const blogApi = {
  // Obtener posts publicados con autor y tags
  async getPublishedPosts() {
    // Primero obtenemos los posts
    const { data: posts, error } = await supabase
      .from('posts')
      .select('*')
      .eq('status', 'PUBLISHED')
      .order('published_at', { ascending: false })

    if (error) {
      console.error('Error obteniendo posts:', error)
      throw error
    }

    if (!posts || posts.length === 0) {
      return []
    }

    // Luego obtenemos los autores y tags por separado
    const postsWithAuthorsAndTags = await Promise.all(
      posts.map(async (post) => {
        // Obtener información del autor
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', post.author_id)
          .single()

        // Obtener tags del post
        const { data: postTags } = await supabase
          .from('post_tags')
          .select(`
            tags (
              name
            )
          `)
          .eq('post_id', post.id)

        const tags = postTags?.map(pt => ({ name: pt.tags?.name })).filter(t => t.name) || []

        return {
          ...post,
          author_name: profile?.full_name || 'Autor Desconocido',
          post_tags: tags
        }
      })
    )

    return postsWithAuthorsAndTags
  },

  // Obtener un post por slug
  async getPostBySlug(slug: string) {
    const { data: post, error } = await supabase
      .from('posts')
      .select('*')
      .eq('slug', slug)
      .eq('status', 'PUBLISHED')
      .single()

    if (error) {
      console.error('Error obteniendo post:', error)
      throw error
    }

    // Obtener información del autor
    const { data: profile } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', post.author_id)
      .single()

    // Obtener las secciones de contenido
    const { data: sections } = await supabase
      .from('content_sections')
      .select('*')
      .eq('post_id', post.id)
      .order('order')

    // Obtener tags del post
    const { data: postTags } = await supabase
      .from('post_tags')
      .select(`
        tags (
          name
        )
      `)
      .eq('post_id', post.id)

    const tags = postTags?.map(pt => ({ name: pt.tags?.name })).filter(t => t.name) || []

    return {
      ...post,
      author_name: profile?.full_name || 'Autor Desconocido',
      content_sections: sections || [],
      post_tags: tags
    }
  },

  // Obtener todos los tags
  async getTags() {
    const { data, error } = await supabase
      .from('tags')
      .select('*')
      .order('name')

    if (error) {
      console.error('Error obteniendo tags:', error)
      throw error
    }

    return data || []
  },

  // Obtener URL de imagen del storage
  getImageUrl(path: string) {
    return supabase.storage.from('blog-images').getPublicUrl(path).data.publicUrl
  },

  // Subir imagen al storage
  async uploadImage(file: File, path: string) {
    const { data, error } = await supabase.storage
      .from('blog-images')
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false
      })

    if (error) {
      console.error('Error subiendo imagen:', error)
      throw error
    }

    return data
  },

  // Obtener todos los posts para admin (incluyendo borradores)
  async getAllPostsForAdmin() {
    const { data, error } = await supabase
      .from('posts')
      .select(`
        id,
        title,
        slug,
        status,
        created_at,
        updated_at,
        published_at
      `)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error obteniendo posts para admin:', error)
      throw error
    }

    // Los datos ya vienen con el formato correcto
    return data || []
  },

  // Test de conexión
  async testStorage() {
    try {
      const { data, error } = await supabase.storage
        .from('blog-images')
        .list('', { limit: 1 })

      if (error) {
        console.warn('Storage test failed:', error)
        return { success: false, error }
      }

      return { success: true, data }
    } catch (error) {
      console.warn('Storage test failed:', error)
      return { success: false, error }
    }
  },

  // Obtener un post para edición (incluye contenido y relaciones)
  async getPostForEditing(postId: string) {
    try {
      // Obtener el post básico
      const { data: post, error: postError } = await supabase
        .from('posts')
        .select('*')
        .eq('id', postId)
        .single()

      if (postError) throw postError

      // Obtener las secciones de contenido
      const { data: sections, error: sectionsError } = await supabase
        .from('content_sections')
        .select('*')
        .eq('post_id', postId)
        .order('order')

      if (sectionsError) throw sectionsError

      // Obtener tags del post
      const { data: postTags } = await supabase
        .from('post_tags')
        .select(`
          tags (
            name
          )
        `)
        .eq('post_id', postId)

      const tags = postTags?.map(pt => pt.tags?.name).filter(name => name) || []

      return {
        ...post,
        content_sections: sections || [],
        tags: tags
      }
    } catch (error) {
      console.error('Error obteniendo post para edición:', error)
      throw error
    }
  },

  // Crear o actualizar un post
  async upsertPost(payload: {
    id?: string | null
    title: string
    slug: string
    status: 'DRAFT' | 'PUBLISHED'
    cover_image_url?: string
    content_sections: Array<{
      id?: string
      order: number
      type: 'HEADING' | 'PARAGRAPH' | 'IMAGE'
      content: any
    }>
    tags: number[]
  }) {
    try {
      let postId = payload.id

      // Si es un post nuevo, crearlo primero
      if (!postId) {
        // Obtener el usuario actual
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Usuario no autenticado')

        const { data: newPost, error: postError } = await supabase
          .from('posts')
          .insert({
            title: payload.title,
            slug: payload.slug,
            status: payload.status,
            cover_image_url: payload.cover_image_url || null,
            author_id: user.id,
            published_at: payload.status === 'PUBLISHED' ? new Date().toISOString() : null
          })
          .select()
          .single()

        if (postError) throw postError
        postId = newPost.id
      } else {
        // Actualizar post existente
        const { error: updateError } = await supabase
          .from('posts')
          .update({
            title: payload.title,
            slug: payload.slug,
            status: payload.status,
            cover_image_url: payload.cover_image_url || null,
            published_at: payload.status === 'PUBLISHED' ? new Date().toISOString() : null,
            updated_at: new Date().toISOString()
          })
          .eq('id', postId)

        if (updateError) throw updateError
      }

      // Eliminar secciones existentes y recrearlas
      await supabase
        .from('content_sections')
        .delete()
        .eq('post_id', postId)

      if (payload.content_sections.length > 0) {
        const sectionsToInsert = payload.content_sections.map(section => ({
          post_id: postId,
          order: section.order,
          type: section.type,
          content: section.content
        }))

        const { error: sectionsError } = await supabase
          .from('content_sections')
          .insert(sectionsToInsert)

        if (sectionsError) throw sectionsError
      }

      // Manejar tags
      // Primero eliminar relaciones existentes
      await supabase
        .from('post_tags')
        .delete()
        .eq('post_id', postId)

      // Insertar nuevas relaciones
      if (payload.tags.length > 0) {
        const tagRelations = payload.tags.map(tagId => ({
          post_id: postId,
          tag_id: tagId
        }))

        const { error: tagsError } = await supabase
          .from('post_tags')
          .insert(tagRelations)

        if (tagsError) throw tagsError
      }

      return { id: postId }
    } catch (error) {
      console.error('Error guardando post:', error)
      throw error
    }
  }
}

// ===== DEMO API =====
// Función helper para enviar solicitudes de demo
export async function submitDemoRequest(formData: {
  nombre: string
  empresa: string
  email: string
  telefono?: string
  mensaje?: string
}) {
  const { data, error } = await supabase
    .from('demo_requests')
    .insert([
      {
        nombre: formData.nombre,
        empresa: formData.empresa,
        email: formData.email,
        telefono: formData.telefono || null,
        mensaje: formData.mensaje || null,
        status: 'pending'
      }
    ])
    .select()

  if (error) {
    console.error('Error guardando en Supabase:', error)
    throw new Error('Error guardando la solicitud: ' + error.message)
  }

  return data
}

// Función para obtener todas las solicitudes de demo (para admin)
export async function getDemoRequests() {
  const { data, error } = await supabase
    .from('demo_requests')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error obteniendo solicitudes de demo:', error)
    throw error
  }

  return data || []
}
